/**
 * Safe localStorage wrapper — falls back to an in-memory store when
 * localStorage is unavailable (Safari Private Mode, restricted environments).
 * Prevents Zustand persist from throwing and leaving the app in a blank state.
 */

const memoryStore: Record<string, string> = {}

function isLocalStorageAvailable(): boolean {
  try {
    const key = '__storage_test__'
    localStorage.setItem(key, '1')
    localStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

const useLocalStorage = isLocalStorageAvailable()

export const safeStorage = {
  getItem: (name: string): string | null => {
    try {
      return useLocalStorage ? localStorage.getItem(name) : (memoryStore[name] ?? null)
    } catch {
      return memoryStore[name] ?? null
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      if (useLocalStorage) {
        localStorage.setItem(name, value)
      } else {
        memoryStore[name] = value
      }
    } catch {
      memoryStore[name] = value
    }
  },
  removeItem: (name: string): void => {
    try {
      if (useLocalStorage) {
        localStorage.removeItem(name)
      } else {
        delete memoryStore[name]
      }
    } catch {
      delete memoryStore[name]
    }
  },
}
